import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ScrollText, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * TosReacceptGate — checks `/api/me/tos` once when the user is signed in.
 * If `needs_reaccept`, shows a non-dismissable modal asking the user to
 * accept the new ToS before continuing. POSTs to `/api/me/tos/accept`
 * to record the acceptance, then steps out of the way.
 *
 * Why a modal (not a page redirect)?
 *   - Less disruptive to in-flight flows (e.g. the user is mid-routine).
 *   - The user can still read the new /terms in a new tab via the link
 *     inside the modal — they don't lose their place.
 *
 * Mounting: included once at the App root, AFTER `<AuthProvider>` so it
 * can read `useAuth()`. Renders nothing for guests / unauthenticated.
 */
const TosReacceptGate = () => {
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();
  const [needsReaccept, setNeedsReaccept] = useState(false);
  const [currentVersion, setCurrentVersion] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Skip the ToS gate for anonymous/stealth guest sessions — they
  // haven't formally signed up yet; ToS acceptance happens at real
  // account creation. Also suppress on the onboarding funnel
  // (/onboarding, /capture, /analyzing) so the modal never covers
  // the 11-step trust-building quiz. Also skip in public demo mode
  // (demo.trubeaute.com) — the demo user is ephemeral, ToS
  // acceptance is irrelevant.
  const email = ((user && user.email) || "").toLowerCase();
  const isDemoUser =
    !!user &&
    (email.endsWith("@demo.trubeaute.local") ||
      user.auth_provider === "demo");
  const isDemoMode =
    typeof window !== "undefined" && !!window.__trubeaute_demo_mode;
  const isGuest =
    !!user &&
    (email.endsWith("@guest.trubeaute.local") ||
      user.auth_provider === "guest");
  const onOnboardingFunnel =
    location.pathname.startsWith("/onboarding") ||
    location.pathname.startsWith("/capture") ||
    location.pathname.startsWith("/analyzing");

  useEffect(() => {
    // Only run once we know the user; skip for guests / unauthenticated.
    if (authLoading || !user) return;
    if (isGuest) return;
    if (isDemoUser || isDemoMode) return;
    if (onOnboardingFunnel) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/me/tos`, {
          withCredentials: true,
        });
        if (cancelled) return;
        setCurrentVersion(data?.current_version || null);
        setNeedsReaccept(!!data?.needs_reaccept);
      } catch (e) {
        // 401 means the cookie is gone — let other parts of the app
        // handle re-auth. Silently no-op here.
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.debug("[tos-gate] /me/tos check failed:", e?.message || e);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authLoading, user, isGuest, onOnboardingFunnel, isDemoUser, isDemoMode]);

  const onAccept = async () => {
    if (submitting || !currentVersion) return;
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/me/tos/accept`,
        { version: currentVersion },
        { withCredentials: true },
      );
      setNeedsReaccept(false);
    } catch (e) {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.debug("[tos-gate] accept failed:", e?.message || e);
      }
      // Leave modal up — user can retry. We don't toast (unauthorized
      // users get redirected by axios interceptors, network blips will
      // resolve on retry).
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {needsReaccept && !isGuest && !onOnboardingFunnel && !isDemoUser && !isDemoMode && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] flex items-center justify-center bg-[#2C2123]/60 backdrop-blur-sm px-4"
          data-testid="tos-reaccept-modal"
        >
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 12, opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="bg-white rounded-3xl p-7 max-w-md w-full soft-shadow-lg"
          >
            <div className="h-11 w-11 rounded-full bg-[#F2E8E9] flex items-center justify-center mb-4">
              <ScrollText className="h-5 w-5 text-[#9B6A6C]" />
            </div>
            <span className="inline-block text-xs uppercase tracking-[0.25em] text-[#9B6A6C] mb-2">
              A small update
            </span>
            <h2 className="text-2xl font-light text-[#2C2123] mb-3">
              Our Terms of Service have changed.
            </h2>
            <p className="text-[#5C4B51] leading-relaxed mb-5">
              Nothing scary — we keep our policies plain-language and
              up-to-date. Please take a look at the latest{" "}
              <Link
                to="/terms"
                target="_blank"
                className="text-[#9B6A6C] underline hover:text-[#805557]"
                data-testid="tos-reaccept-read-link"
              >
                Terms of Service
              </Link>
              {" "}and tap below to keep using TruBeauté. Your data and
              routines aren't affected.
            </p>
            <button
              onClick={onAccept}
              disabled={submitting || !currentVersion}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#9B6A6C] text-white hover:bg-[#805557] disabled:opacity-60 rounded-full px-6 py-3.5 transition-all shadow-[0_6px_20px_rgba(155,106,108,0.35)]"
              data-testid="tos-reaccept-accept-btn"
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              I've read and accept
            </button>
            <p className="text-xs text-[#8A7D82] text-center mt-4">
              Version {currentVersion}
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default TosReacceptGate;
